import { $themeConfig } from '@themeConfig'
import {
  applyTheme, cacheAccent, currentCommunityId, normaliseAccent, readCachedAccent, readStoredTheme, resolveMode, saveMode,
} from '@/theme/applyTheme'
import { DEFAULT_ACCENT } from '@/theme/presets'
import { getCommunityAppearance, updateCommunityAppearance } from '@/apiManager/community'

const storedTheme = readStoredTheme()

// Sign-in, sign-up and onboarding (the `full` layout) are designed around the
// brand, so they always show the default theme; the admin's choice is kept
// and comes back inside the dashboard.
const BRAND_THEME = { mode: 'light', accent: DEFAULT_ACCENT, custom: null }

// Light / dark and the accent go through the theme (src/theme); the result
// lands in `skin` too, so Vuexy code reading `skin === 'dark'` stays right.
function showTheme(state) {
  state.layout.skin = applyTheme(state.layout.brandLocked ? BRAND_THEME : state.layout.theme)
}

// The mode is this device's; the accent is the community's, cached here and
// saved on the server by setCommunityAccent below.
function setTheme(state, patch) {
  Object.assign(state.layout.theme, patch)
  if ('mode' in patch) saveMode(state.layout.theme.mode)
  if ('accent' in patch || 'custom' in patch) cacheAccent(state.layout.theme)
  showTheme(state)
}

const sameAccent = (a, b) => a.accent === b.accent && JSON.stringify(a.custom) === JSON.stringify(b.custom)
// The api() wrapper resolves HTTP errors with their body rather than rejecting.
const succeeded = response => Boolean(response) && response.statusCode >= 200 && response.statusCode < 300

export default {
  namespaced: true,
  state: {
    layout: {
      isRTL: $themeConfig.layout.isRTL,
      skin: resolveMode(storedTheme.mode) === 'dark' ? 'dark' : $themeConfig.layout.skin,
      theme: storedTheme,
      brandLocked: false,
      routerTransition: $themeConfig.layout.routerTransition,
      type: $themeConfig.layout.type,
      contentWidth: $themeConfig.layout.contentWidth,
      menu: {
        hidden: $themeConfig.layout.menu.hidden,
      },
      navbar: {
        type: $themeConfig.layout.navbar.type,
        backgroundColor: $themeConfig.layout.navbar.backgroundColor,
      },
      footer: {
        type: $themeConfig.layout.footer.type,
      },
    },
  },
  getters: {},
  mutations: {
    TOGGLE_RTL(state) {
      state.layout.isRTL = !state.layout.isRTL
      document.documentElement.setAttribute('dir', state.layout.isRTL ? 'rtl' : 'ltr')
    },
    UPDATE_SKIN(state, skin) {
      if (skin === 'dark' || skin === 'light') setTheme(state, { mode: skin })
      // Vuexy's 'bordered' / 'semi-dark' are layout classes, not a colour mode.
      else state.layout.skin = skin
    },
    // { mode?, accent?, custom? }: saved, then applied.
    UPDATE_THEME(state, patch) {
      setTheme(state, patch)
    },
    // Re-applies without saving: at boot, and when the OS flips light / dark.
    APPLY_THEME(state) {
      showTheme(state)
    },
    // true while a `full` layout page is open (App.vue).
    LOCK_BRAND_THEME(state, locked) {
      if (state.layout.brandLocked === locked) return
      state.layout.brandLocked = locked
      showTheme(state)
    },
    UPDATE_ROUTER_TRANSITION(state, val) {
      state.layout.routerTransition = val
    },
    UPDATE_LAYOUT_TYPE(state, val) {
      state.layout.type = val
    },
    UPDATE_CONTENT_WIDTH(state, val) {
      state.layout.contentWidth = val
    },
    UPDATE_NAV_MENU_HIDDEN(state, val) {
      state.layout.menu.hidden = val
    },
    UPDATE_NAVBAR_CONFIG(state, obj) {
      Object.assign(state.layout.navbar, obj)
    },
    UPDATE_FOOTER_CONFIG(state, obj) {
      Object.assign(state.layout.footer, obj)
    },
  },
  actions: {
    // On entering the dashboard (at boot, or after Switch Account): the
    // community's cached accent at once, then the server's.
    async syncCommunityAccent({ state, commit }) {
      const communityId = currentCommunityId()
      const cached = readCachedAccent(communityId)
      if (!sameAccent(cached, state.layout.theme)) commit('UPDATE_THEME', cached)
      try {
        const response = await getCommunityAppearance()
        // Switched to another community meanwhile: this answer is not its.
        if (currentCommunityId() !== communityId) return
        if (!succeeded(response) || !response.data) return
        const remote = normaliseAccent(response.data)
        if (!sameAccent(remote, state.layout.theme)) commit('UPDATE_THEME', remote)
      } catch (e) {
        // Offline, or a server without the endpoint yet: keep the cached accent.
      }
    },
    // Shows the accent at once and saves it for everyone in the community,
    // putting the previous one back if the save fails. Rejects on failure so
    // the picker can say so.
    async setCommunityAccent({ state, commit }, { accent, custom = null }) {
      const previous = { accent: state.layout.theme.accent, custom: state.layout.theme.custom }
      const next = normaliseAccent({ accent, custom })
      commit('UPDATE_THEME', next)
      let response
      try {
        response = await updateCommunityAppearance({
          accent: next.accent === DEFAULT_ACCENT ? null : next.accent,
          color: next.custom ? next.custom.primary : null,
        })
      } catch (e) {
        response = null
      }
      if (!succeeded(response)) {
        commit('UPDATE_THEME', previous)
        throw new Error('The accent could not be saved.')
      }
    },
  },
}
